import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Download, Wand2, ChevronRight, ChevronDown, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MultiBrandSelect from "@/components/common/MultiBrandSelect";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import DatePicker from "@/components/common/DatePicker";
import ActiveUploadsPanel from "@/components/ops/ActiveUploadsPanel";
import UploadCoverageCard from "@/components/ops/UploadCoverageCard";
import { DEFAULT_UPLOADS_RANGE } from "@/lib/dateDefaults";

function toCSV(rows, cols) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = cols.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c.key])).join(",")).join("\n");
  return `${head}\n${body}`;
}

function downloadCSV(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function OpsUploadsPage() {
  // Default: today only. Ops uploads are typically same-day, so an empty
  // filter would surface a very long historical list on every visit.
  const _range = DEFAULT_UPLOADS_RANGE();
  const [uploads, setUploads] = useState([]);
  const [ctx, setCtx] = useState({ brands: [] });
  const [brandIds, setBrandIds] = useState([]); // [] = all
  const [dateFrom, setDateFrom] = useState(_range.from);
  const [dateTo, setDateTo] = useState(_range.to);
  const [loading, setLoading] = useState(true);
  const [daily, setDaily] = useState({ days: [], credit_reason_breakdown: [] });
  const [dailyLoading, setDailyLoading] = useState(false);
  const [retagging, setRetagging] = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);
  const [agentRows, setAgentRows] = useState({});
  const [agentLoading, setAgentLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // upload object
  const [deleting, setDeleting] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get("/ops/my-context").then((r) => setCtx(r.data)); }, []);

  /** Convert current brandIds selection into a single API brand_id when possible.
   *  API accepts one brand at a time; multi-select filters uploads list client-side. */
  const singleBrandId = brandIds.length === 1 ? brandIds[0] : null;

  const loadUploads = useCallback(() => {
    setLoading(true);
    const params = {};
    if (singleBrandId) params.brand_id = singleBrandId;
    api.get("/ops/uploads", { params })
      .then((r) => setUploads(r.data))
      .finally(() => setLoading(false));
  }, [singleBrandId]);

  const loadDaily = useCallback(() => {
    setDailyLoading(true);
    const params = {};
    if (singleBrandId) params.brand_id = singleBrandId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/ops/analytics/daily-summary", { params })
      .then((r) => setDaily(r.data || { days: [], credit_reason_breakdown: [] }))
      .catch(() => setDaily({ days: [], credit_reason_breakdown: [] }))
      .finally(() => setDailyLoading(false));
  }, [singleBrandId, dateFrom, dateTo]);

  useEffect(() => { loadUploads(); loadDaily(); }, [loadUploads, loadDaily]);

  // Client-side filter for uploads when multi-brand selection is active
  const filteredUploads = brandIds.length >= 2
    ? uploads.filter((u) => brandIds.includes(String(u.brand_id)))
    : uploads;

  const toggleAgentRow = async (date) => {
    if (expandedDate === date) { setExpandedDate(null); return; }
    setExpandedDate(date);
    if (!agentRows[date]) {
      setAgentLoading(true);
      try {
        const params = { date_from: date, date_to: date };
        if (singleBrandId) params.brand_id = singleBrandId;
        const r = await api.get("/ops/analytics/agents", { params });
        setAgentRows((prev) => ({ ...prev, [date]: r.data || [] }));
      } catch {
        setAgentRows((prev) => ({ ...prev, [date]: [] }));
      } finally { setAgentLoading(false); }
    }
  };

  const runRetag = async () => {
    setRetagging(true);
    try {
      const url = singleBrandId ? `/ops/uploads/retag?brand_id=${singleBrandId}` : "/ops/uploads/retag";
      const r = await api.post(url);
      const rc = r.data.credit_reason_counts || {};
      const top = Object.entries(rc).sort((a, b) => b[1] - a[1]).slice(0, 3)
        .map(([k, v]) => `${k}: ${v}`).join(" · ");
      toast.success(
        `Retag done — ${r.data.retagged} scanned, ${r.data.changed} updated across ${r.data.brands_processed || 1} brand(s) in ${r.data.duration_s}s`,
        { description: top, duration: 12000 }
      );
      loadDaily();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Retag failed");
    } finally { setRetagging(false); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/ops/uploads/${pendingDelete.id}`);
      toast.success(`Upload deleted — ${r.data.rows_deleted || 0} rows removed.`);
      setPendingDelete(null);
      loadUploads();
      loadDaily();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const exportCSV = () => {
    const cols = [
      { key: "date", label: "Date" },
      { key: "active_agents", label: "Agents active" },
      { key: "total_calls", label: "Total calls" },
      { key: "unique_users_called", label: "Users called" },
      { key: "fresh_calls", label: "Fresh calls" },
      { key: "followup_calls", label: "Follow-up calls" },
      { key: "unique_users_contactable", label: "Contactable users" },
      { key: "unique_users_converted", label: "Users converted" },
      { key: "conversion_pct", label: "Conversion %" },
      { key: "conversion_of_contactable_pct", label: "Conv % of contactable" },
    ];
    const brandLabel =
      brandIds.length === 0 ? "all-brands"
      : brandIds.length === 1 ? brandIds[0]
      : `${brandIds.length}-brands`;
    const rangeLabel = dateFrom || dateTo ? `-${dateFrom || "…"}_to_${dateTo || "…"}` : "";
    downloadCSV(`daily-summary-${brandLabel}${rangeLabel}.csv`, toCSV(daily.days || [], cols));
  };

  const totalRows = (daily.credit_reason_breakdown || []).reduce((n, r) => n + (r.count || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">Ops uploads</h1>
          <p className="text-sm text-slate-600 mt-1">Daily call-report uploads from your assigned brands.</p>
        </div>
        <div className="flex gap-3 items-end flex-wrap" data-testid="uploads-filter-row">
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Brand</Label>
            <MultiBrandSelect
              brands={ctx.brands}
              value={brandIds}
              onChange={setBrandIds}
              testId="uploads-brand-multi"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">From</Label>
            <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="Pick from date"
              className="w-[170px]" testId="uploads-date-from" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">To</Label>
            <DatePicker value={dateTo} onChange={setDateTo} placeholder="Pick to date"
              className="w-[170px]" testId="uploads-date-to" />
          </div>
          <Button onClick={() => nav("/ops/upload")} className="btn-tactile h-9"
            style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }} data-testid="uploads-new-upload">
            Upload
          </Button>
        </div>
      </div>

      {brandIds.length >= 2 && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 inline-block">
          {brandIds.length} brands selected — uploads list filtered client-side; daily summary shows the aggregate across all your brands. Select exactly one brand to scope daily numbers to it.
        </div>
      )}

      {/* Live progress: any upload still running (this user's or all, if admin).
          Re-attaches on tab reload — the source of truth is ops_upload_progress in Mongo. */}
      <ActiveUploadsPanel
        onCompleted={(u) => {
          if (u.phase === "complete") {
            loadUploads();
            loadDaily();
          }
        }}
      />

      <UploadCoverageCard brandIds={brandIds} />

      <LoadingOverlay loading={dailyLoading} testId="daily-summary-loading">
      <div className="glass-card p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <h2 className="font-medium text-slate-900" data-testid="daily-summary-title">Daily summary</h2>
            <p className="text-xs text-slate-500">Per-day rollup. Range: {dateFrom || "start"} → {dateTo || "today"}.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadDaily} disabled={dailyLoading}
              data-testid="daily-summary-refresh" className="text-xs">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${dailyLoading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={exportCSV} disabled={(daily.days || []).length === 0}
              data-testid="daily-summary-csv" className="text-xs">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button variant="ghost" size="sm" onClick={runRetag} disabled={retagging}
              data-testid="daily-summary-retag" className="text-xs text-amber-700 hover:text-amber-800">
              <Wand2 className={`w-3.5 h-3.5 mr-1 ${retagging ? "animate-spin" : ""}`} /> Retag {!singleBrandId ? "(all brands)" : ""}
            </Button>
          </div>
        </div>

        {dailyLoading ? (
          <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (daily.days || []).length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">No daily data yet — upload a call report to populate.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="daily-summary-table">
              <thead>
                <tr className="text-slate-500 text-[10px] uppercase">
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-right py-2 px-2">Agents</th>
                  <th className="text-right py-2 px-2" title="Total call attempts (each call counted)">Total calls</th>
                  <th className="text-right py-2 px-2" title="Unique users called on that date">Users called</th>
                  <th className="text-right py-2 px-2">Fresh</th>
                  <th className="text-right py-2 px-2">Follow-ups</th>
                  <th className="text-right py-2 px-2" title="Unique contactable users">Contactable users</th>
                  <th className="text-right py-2 px-2" title="Unique users who converted (deposited on/after call)">Converted</th>
                  <th className="text-right py-2 px-2" title="Converted ÷ Users called">Conv %</th>
                  <th className="text-right py-2 px-2" title="Converted ÷ Contactable users">Conv % (contactable)</th>
                </tr>
              </thead>
              <tbody>
                {(daily.days || []).map((d) => (
                  <React.Fragment key={d.date}>
                    <tr
                      className="border-t border-white/60 hover:bg-white/50 cursor-pointer"
                      onClick={() => toggleAgentRow(d.date)}
                      data-testid={`daily-summary-row-${d.date}`}
                    >
                      <td className="py-1.5 px-2 font-medium flex items-center gap-1">
                        {expandedDate === d.date
                          ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                          : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                        {d.date}
                      </td>
                      <td className="py-1.5 px-2 text-right">{d.active_agents}</td>
                      <td className="py-1.5 px-2 text-right">{(d.total_calls || 0).toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right">{(d.unique_users_called || 0).toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right text-emerald-700">{(d.fresh_calls || 0).toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right text-blue-700">{(d.followup_calls || 0).toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right">{(d.unique_users_contactable || 0).toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right font-semibold">{(d.unique_users_converted || 0).toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right">{d.conversion_pct}%</td>
                      <td className="py-1.5 px-2 text-right">{d.conversion_of_contactable_pct}%</td>
                    </tr>
                    {expandedDate === d.date && (
                      <tr className="bg-slate-50/70">
                        <td colSpan={10} className="p-3">
                          {agentLoading && !agentRows[d.date] ? (
                            <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin" /></div>
                          ) : (agentRows[d.date] || []).length === 0 ? (
                            <div className="text-xs text-slate-500 text-center py-3">No per-agent activity for {d.date}.</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs" data-testid={`agent-drilldown-${d.date}`}>
                                <thead>
                                  <tr className="text-slate-500 text-[10px] uppercase">
                                    <th className="text-left py-1 px-2">Agent</th>
                                    <th className="text-right py-1 px-2">Calls</th>
                                    <th className="text-right py-1 px-2">Contactable</th>
                                    <th className="text-right py-1 px-2">Fresh</th>
                                    <th className="text-right py-1 px-2">Follow-ups</th>
                                    <th className="text-right py-1 px-2">Converted</th>
                                    <th className="text-right py-1 px-2">Conv %</th>
                                    <th className="text-right py-1 px-2" title="Percent of this agent's calls dispositioned as 'wrong number'. >30% is unusual — bad lead data.">Wrong number %</th>
                                    <th className="text-right py-1 px-2">Interested %</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(agentRows[d.date] || []).map((a) => (
                                    <tr key={a.agent_id || a.agent_name} className="border-t border-white/60">
                                      <td className="py-1 px-2 font-medium">{a.agent_name || a.agent_id || "—"}</td>
                                      <td className="py-1 px-2 text-right">{(a.total_calls || 0).toLocaleString()}</td>
                                      <td className="py-1 px-2 text-right">{(a.contactable || 0).toLocaleString()}</td>
                                      <td className="py-1 px-2 text-right text-emerald-700">{(a.fresh || 0).toLocaleString()}</td>
                                      <td className="py-1 px-2 text-right text-blue-700">{(a.followup || 0).toLocaleString()}</td>
                                      <td className="py-1 px-2 text-right font-semibold">{(a.converted || 0).toLocaleString()}</td>
                                      <td className="py-1 px-2 text-right">{a.conversion_pct}%</td>
                                      <td className="py-1 px-2 text-right">{a.wrong_number_pct}%</td>
                                      <td className="py-1 px-2 text-right">{a.interested_pct}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Diagnostic: why conversions are what they are */}
        {(daily.credit_reason_breakdown || []).length > 0 && (
          <details className="mt-4 text-xs" data-testid="credit-reason-breakdown">
            <summary className="cursor-pointer text-slate-500 hover:text-slate-800">
              Credit-reason breakdown ({totalRows.toLocaleString()} rows) — click to expand
            </summary>
            <div className="mt-2 space-y-1">
              {daily.credit_reason_breakdown.map((r, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.credit_status === "yes" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                    {r.credit_status}
                  </span>
                  <span className="text-slate-600 flex-1 truncate">{r.credit_reason}</span>
                  <span className="text-slate-500 font-medium">{r.count.toLocaleString()}</span>
                  <span className="text-slate-400 text-[10px] w-10 text-right">
                    {totalRows ? Math.round((r.count / totalRows) * 100) : 0}%
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
      </LoadingOverlay>

      <LoadingOverlay loading={loading} testId="uploads-list-loading">
      <div className="glass-card p-4">
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : filteredUploads.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            {uploads.length === 0 ? "No uploads yet." : "No uploads match the selected brand filter."}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="uploads-table">
            <thead>
              <tr className="text-slate-500 text-xs uppercase">
                <th className="text-left py-2 px-3">Brand</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Filename</th>
                <th className="text-left py-2 px-3">Rows</th>
                <th className="text-left py-2 px-3">Uploaded by</th>
                <th className="text-left py-2 px-3">When</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredUploads.map((u) => (
                <tr key={u.id} className="border-t border-white/60 hover:bg-white/50">
                  <td className="py-2 px-3 font-medium">{u.brand_name}</td>
                  <td className="py-2 px-3">
                    {u.report_type ? (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${u.report_type === "FTD" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                        {u.report_type}
                      </span>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="py-2 px-3 text-xs">{u.filename}</td>
                  <td className="py-2 px-3">{u.row_count}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{u.ops_user_email}</td>
                  <td className="py-2 px-3 text-xs text-slate-500">{new Date(u.uploaded_at).toLocaleString()}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <Link to={`/ops/uploads/${u.id}`} className="text-[#0A84FF] mr-3" data-testid={`upload-open-${u.id}`}>Open</Link>
                    <button
                      onClick={() => setPendingDelete(u)}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs"
                      data-testid={`upload-delete-${u.id}`}
                      title="Delete this upload and all its rows"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </LoadingOverlay>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="upload-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this upload?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  This will permanently remove the upload{" "}
                  <b>{pendingDelete.filename}</b> ({pendingDelete.brand_name}) and all{" "}
                  <b>{pendingDelete.row_count?.toLocaleString()}</b> associated call rows.
                  Analytics numbers will refresh to exclude this data. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="upload-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="upload-delete-confirm"
            >
              {deleting ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Deleting…</> : "Delete upload"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
